import { createContext, useContext, useMemo, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { features } from '@/config/features';
import { supabase } from '@/integrations/supabase/client';

interface FeatureFlagContextType {
  isFeatureEnabled: (featureName: string) => boolean;
  isAdmin: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Check admin status from database using server-side role validation
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) {
        setIsAdmin(false);
        return;
      }
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      
      if (error) {
        setIsAdmin(false);
        return;
      }
      
      setIsAdmin(!!data);
    };
    
    checkAdminStatus();
  }, [user?.id]);

  const isFeatureEnabled = useMemo(() => {
    return (featureName: string): boolean => {
      const enabled = features[featureName];
      
      // Feature not configured - default to disabled
      if (enabled === undefined) {
        return false;
      }
      
      // Admin override - always enabled
      if (isAdmin) {
        return true;
      }
      
      return enabled;
    };
  }, [isAdmin]);

  const value: FeatureFlagContextType = {
    isFeatureEnabled,
    isAdmin,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}
